import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { History, ChevronUp, ChevronDown, Minus } from 'lucide-react';
import { API_URL } from '../utils/apiUrl';

const formatDateTR = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const Delta = ({ oldScore, newScore }) => {
  if (oldScore == null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-neutral-500" data-testid="score-delta-initial">
        <Minus className="w-3 h-3" /> İlk değerlendirme
      </span>
    );
  }
  const diff = (newScore - oldScore);
  if (Math.abs(diff) < 0.001) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-neutral-500">
        <Minus className="w-3 h-3" /> Değişiklik yok
      </span>
    );
  }
  const up = diff > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${up ? 'text-emerald-700' : 'text-red-600'}`} data-testid={up ? 'score-delta-up' : 'score-delta-down'}>
      {up ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      {up ? '+' : ''}{diff.toFixed(1)}
    </span>
  );
};

const ScoreHistoryTimeline = ({ productId }) => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!productId) return;
    axios.get(`${API_URL}/api/products/${productId}/score-history`)
      .then(r => setHistory(r.data || []))
      .catch(() => setHistory([]));
  }, [productId]);

  if (history == null) {
    return (
      <div className="border border-neutral-200 rounded-md bg-white p-6 shadow-sm" data-testid="guveniz-loading">
        <p className="text-sm text-neutral-400">Güvenİzi yükleniyor...</p>
      </div>
    );
  }

  if (history.length === 0) {
    return null;
  }

  // Reverse chronological order from API (newest first)
  const items = history;

  return (
    <section className="border border-neutral-200 rounded-md bg-white p-6 md:p-8 shadow-sm mb-8" data-testid="guveniz-section">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-full bg-[#1C3B2A]/10 flex items-center justify-center">
          <History className="w-4 h-4 text-[#1C3B2A]" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-black tracking-tight text-[#111]">Güvenİzi</h3>
          <p className="text-xs text-neutral-500">Bu ürünün puan geçmişi ve değerlendirme değişiklikleri</p>
        </div>
      </div>

      {items.length === 1 ? (
        <div className="mt-5 border-l-2 border-[#1C3B2A] pl-5 py-2" data-testid="guveniz-single">
          <p className="text-xs font-bold uppercase text-neutral-400 mb-1">{formatDateTR(items[0].created_at)}</p>
          <p className="text-sm font-bold text-[#1C3B2A] mb-2">{items[0].source}</p>
          <p className="text-sm font-semibold text-neutral-700">Bu ürün ilk test sonucuna göre puanlanmıştır.</p>
          {items[0].admin_note && (
            <p className="text-xs text-neutral-500 italic mt-2">&ldquo;{items[0].admin_note}&rdquo;</p>
          )}
        </div>
      ) : (
        <ol className="mt-5 relative border-l-2 border-neutral-200 pl-5 space-y-6" data-testid="guveniz-timeline">
          {items.map((h, idx) => (
            <li key={h.id} className="relative" data-testid={`guveniz-item-${idx}`}>
              <span className={`absolute -left-[27px] w-3 h-3 rounded-full ${idx === 0 ? 'bg-[#1C3B2A] ring-4 ring-[#1C3B2A]/20' : 'bg-neutral-300'}`} />
              <p className="text-xs font-bold uppercase text-neutral-400 mb-1">{formatDateTR(h.created_at)}</p>
              <div className="flex items-center gap-3 flex-wrap mb-1">
                {h.old_score != null && (
                  <span className="text-sm font-mono font-semibold text-neutral-400 line-through">{Number(h.old_score).toFixed(1)}</span>
                )}
                <span className="text-base font-mono font-black text-[#111]">{Number(h.new_score).toFixed(1)} <span className="text-xs text-neutral-400">/ 10</span></span>
                <Delta oldScore={h.old_score} newScore={h.new_score} />
              </div>
              <p className="text-sm font-bold text-[#1C3B2A] mb-1">{h.source}</p>
              <p className="text-sm text-neutral-700">{h.change_reason}</p>
              {h.admin_note && (
                <p className="text-xs text-neutral-500 italic mt-1.5 bg-neutral-50 border border-neutral-100 rounded px-3 py-2">{h.admin_note}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default ScoreHistoryTimeline;
